"use client";

import ChatIcon from "@mui/icons-material/Chat";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TimerIcon from "@mui/icons-material/Timer";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import PageContainer from "@/components/atoms/PageContainer";
import PageLayout from "@/components/templates/PageLayout";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetOperationalDashboard from "@/hooks/requests/dashboard/service/use-get-operational-dashboard";
import useGetServiceQueues from "@/hooks/requests/service-queue/use-get-service-queues";
import useGetTags from "@/hooks/requests/tags/use-get-tags";
import useGetWorkflows from "@/hooks/requests/wokflow/use-get-workflow";
import IServiceQueue from "@/interfaces/service-queue.interface";
import { ITag } from "@/interfaces/tag.interface";
import { IWorkflow } from "@/interfaces/workflow.interface";
import { ServiceDashboardMessages } from "@/messages/serviceDashboard/service-dashboard.messages";
import { OperationalKpi } from "@/services/service-dashboard.service";
import { BreadCrumb } from "@/types/page.types";

import AreaChartCard from "./components/AreaChartCard";
import BacklogCard from "./components/BacklogCard";
import DashboardHeader, { FilterOption } from "./components/DashboardHeader";
import DonutCard from "./components/DonutCard";
import KpiCard from "./components/KpiCard";
import RankingCard from "./components/RankingCard";
import SlaCard from "./components/SlaCard";
import { DashboardColor } from "./components/styles";

type ServiceDashboardProps = {
  messages: ServiceDashboardMessages;
};

const MAX_LIST_ITEMS = 5;

const formatDuration = (seconds: number): string => {
  const total = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(total / 60);
  const remainingSeconds = total % 60;
  return `${minutes}m ${remainingSeconds.toString().padStart(2, "0")}s`;
};

const formatNumber = (value: number): string =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(value);

const formatDecimal = (value: number): string =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 }).format(value ?? 0);

export default function ServiceDashboard({ messages }: ServiceDashboardProps) {
  const { lang } = useLanguage();
  const [searchParams] = useSearchParams();
  const isFullscreen = searchParams.get("fullscreen") === "1";

  const [selectedQueues, setSelectedQueues] = useState<FilterOption[]>([]);
  const [selectedTags, setSelectedTags] = useState<FilterOption[]>([]);
  const [selectedWorkflows, setSelectedWorkflows] = useState<FilterOption[]>([]);
  const [appliedFilters, setAppliedFilters] = useState({
    queueIds: [] as number[],
    workflowIds: [] as number[],
    tagNames: [] as string[],
  });
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);

  const { data, isLoading, isRefreshing, error, refresh } = useGetOperationalDashboard({
    queueIds: appliedFilters.queueIds.length ? appliedFilters.queueIds : undefined,
    workflowIds: appliedFilters.workflowIds.length ? appliedFilters.workflowIds : undefined,
    tagNames: appliedFilters.tagNames.length ? appliedFilters.tagNames : undefined,
    autoRefresh,
  });

  const wasRefreshingRef = useRef(false);
  useEffect(() => {
    if (data && !isRefreshing) setLastUpdatedAt(new Date());
    wasRefreshingRef.current = isRefreshing;
  }, [data, isRefreshing]);

  const { data: queuesData } = useGetServiceQueues({ pageNumber: 1, pageSize: 150 });
  const { data: tagsData } = useGetTags({ pageNumber: 1, pageSize: 150, filters: [] });
  const { data: workflowsData } = useGetWorkflows({ pageNumber: 1, pageSize: 150, filters: [] });

  const queueOptions: FilterOption[] = useMemo(
    () =>
      (queuesData?.items ?? []).map((queue: IServiceQueue) => ({ id: queue.id, name: queue.name })),
    [queuesData]
  );
  const tagOptions: FilterOption[] = useMemo(
    () => (tagsData?.items ?? []).map((tag: ITag) => ({ id: tag.id, name: tag.name })),
    [tagsData]
  );
  const workflowOptions: FilterOption[] = useMemo(
    () =>
      (workflowsData?.items ?? []).map((workflow: IWorkflow) => ({
        id: workflow.id,
        name: workflow.name,
      })),
    [workflowsData]
  );

  useEffect(() => {
    if (!isFullscreen || !document.documentElement.requestFullscreen) return;
    document.documentElement.requestFullscreen().catch(() => undefined);
  }, [isFullscreen]);

  const handleApplyFilters = () => {
    setAppliedFilters({
      queueIds: selectedQueues.map((item) => item.id),
      workflowIds: selectedWorkflows.map((item) => item.id),
      tagNames: selectedTags.map((item) => item.name),
    });
  };

  const handleClearFilters = () => {
    setSelectedQueues([]);
    setSelectedTags([]);
    setSelectedWorkflows([]);
    setAppliedFilters({ queueIds: [], workflowIds: [], tagNames: [] });
  };

  const handleFullscreen = () => {
    const url = `${ROUTE_URLS.serviceDashboard(lang)}?fullscreen=1`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const BREAD_CRUMBS: BreadCrumb[] = [
    { title: messages.breadCrumbTitle, href: ROUTE_URLS.serviceDashboard(lang) },
    { title: messages.sub_customer_service, href: ROUTE_URLS.serviceDashboard(lang) },
    { title: messages.operationalBreadCrumb, href: ROUTE_URLS.serviceDashboard(lang) },
  ];

  const errorInfo = {
    shouldLoadError: !data && !!error && !isLoading,
    title: messages.errorToLoad,
    description: messages.noDataMessage,
    alt: messages.notFoundAlt,
  };

  const renderKpi = (
    title: string,
    kpi: OperationalKpi | undefined,
    formatter: (value: number) => string,
    icon: React.ReactNode,
    iconColor: DashboardColor,
    index: number
  ) => (
    <KpiCard
      title={title}
      value={kpi?.value ?? 0}
      formatter={formatter}
      icon={icon}
      iconColor={iconColor}
      index={index}
      compact
      isLoading={isLoading}
      isRefreshing={isRefreshing}
      errorInfo={errorInfo}
    />
  );

  const gridSx = (columns: number) => ({
    display: "grid",
    gap: 1.5,
    gridTemplateColumns: {
      xs: "1fr",
      sm: "repeat(2, 1fr)",
      lg: `repeat(${Math.min(columns, 3)}, 1fr)`,
      xl: `repeat(${columns}, 1fr)`,
    },
  });

  return (
    <PageLayout
      messages={messages}
      title={isFullscreen ? undefined : messages.pageTitle}
      hideBoldTitle
      breadCrumbs={isFullscreen ? [] : BREAD_CRUMBS}
      fullscreen={isFullscreen}
    >
      <PageContainer>
        <DashboardHeader
          messages={messages}
          queueOptions={queueOptions}
          tagOptions={tagOptions}
          workflowOptions={workflowOptions}
          selectedQueues={selectedQueues}
          selectedTags={selectedTags}
          selectedWorkflows={selectedWorkflows}
          onChangeQueues={setSelectedQueues}
          onChangeTags={setSelectedTags}
          onChangeWorkflows={setSelectedWorkflows}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
          autoRefresh={autoRefresh}
          onToggleAutoRefresh={setAutoRefresh}
          onRefresh={refresh}
          lastUpdatedAt={lastUpdatedAt}
          isRefreshing={isRefreshing}
          isFullscreen={isFullscreen}
          onFullscreen={handleFullscreen}
        />

        <MDBox display="flex" flexDirection="column" gap={1.5}>
          <MDBox sx={gridSx(4)}>
            {renderKpi(
              messages.activeConversationsTitle,
              data?.activeConversations,
              formatNumber,
              <ChatIcon fontSize="small" />,
              "success",
              0
            )}
            {renderKpi(
              messages.newConversationsTitle,
              data?.newConversationsToday,
              formatNumber,
              <ChatBubbleOutlineIcon fontSize="small" />,
              "info",
              1
            )}
            {renderKpi(
              messages.closedConversationsTitle,
              data?.closedConversationsToday,
              formatNumber,
              <CheckCircleOutlineIcon fontSize="small" />,
              "dark",
              2
            )}
            {renderKpi(
              messages.unansweredConversationsTitle,
              data?.unansweredConversations,
              formatNumber,
              <HourglassEmptyIcon fontSize="small" />,
              "warning",
              3
            )}
            {renderKpi(
              messages.unreadMessagesTitle,
              data?.unreadMessages,
              formatNumber,
              <MarkEmailUnreadIcon fontSize="small" />,
              "error",
              4
            )}

            {renderKpi(
              messages.frtTitle,
              data?.firstResponseTimeSeconds,
              formatDuration,
              <ScheduleIcon fontSize="small" />,
              "success",
              0
            )}
            <SlaCard
              title={messages.slaTitle}
              value={data?.responseSlaPercent?.value ?? 0}
              isLoading={isLoading}
              isRefreshing={isRefreshing}
            />
            {renderKpi(
              messages.tmaTitle,
              data?.averageHandlingTimeSeconds,
              formatDuration,
              <TimerIcon fontSize="small" />,
              "dark",
              2
            )}
          </MDBox>

          <MDBox sx={gridSx(4)}>
            <BacklogCard
              title={messages.backlogTitle}
              items={(data?.backlog ?? []).slice(0, MAX_LIST_ITEMS)}
              emptyLabel={messages.noDataMessage}
              isLoading={isLoading}
              isRefreshing={isRefreshing}
            />
            <RankingCard
              title={messages.conversationsByQueueTitle}
              items={(data?.conversationsByQueue ?? []).slice(0, MAX_LIST_ITEMS).map((item) => ({
                label: item.name,
                value: item.count,
              }))}
              color="primary"
              emptyLabel={messages.noDataMessage}
              isLoading={isLoading}
              isRefreshing={isRefreshing}
            />
            <RankingCard
              title={messages.queueCapacityTitle}
              items={(data?.queueCapacity ?? []).slice(0, MAX_LIST_ITEMS).map((item) => ({
                label: item.name,
                value: item.openConversations,
                caption: [
                  `${messages.attendantsLabel}: ${item.attendants}`,
                  `${messages.openConversationsLabel}: ${item.openConversations}`,
                  `${formatDecimal(item.conversationsPerAttendant)} ${messages.conversationsPerAttendantUnit}`,
                ].join(" · "),
              }))}
              color="info"
              emptyLabel={messages.noDataMessage}
              isLoading={isLoading}
              isRefreshing={isRefreshing}
            />
            <DonutCard
              title={messages.conversationsByWorkflowTitle}
              items={(data?.conversationsByWorkflow ?? []).slice(0, MAX_LIST_ITEMS)}
              totalLabel={messages.totalLabel}
              emptyLabel={messages.noDataMessage}
              isLoading={isLoading}
              isRefreshing={isRefreshing}
            />
          </MDBox>

          <MDBox sx={gridSx(4)}>
            <RankingCard
              title={messages.conversationsByAttendantTitle}
              items={(data?.conversationsByAttendant ?? [])
                .slice(0, MAX_LIST_ITEMS)
                .map((item) => ({ label: item.name, value: item.count }))}
              color="success"
              emptyLabel={messages.noDataMessage}
              isLoading={isLoading}
              isRefreshing={isRefreshing}
            />
            <RankingCard
              title={messages.unreadByQueueTitle}
              items={(data?.unreadByQueue ?? []).slice(0, MAX_LIST_ITEMS).map((item) => ({
                label: item.name,
                value: item.count,
              }))}
              color="warning"
              emptyLabel={messages.noDataMessage}
              isLoading={isLoading}
              isRefreshing={isRefreshing}
            />
            <DonutCard
              title={messages.aiVsHumanTitle}
              items={data?.aiVsHuman ?? []}
              totalLabel={messages.totalLabel}
              emptyLabel={messages.noDataMessage}
              isLoading={isLoading}
              isRefreshing={isRefreshing}
            />
            <AreaChartCard
              title={messages.conversationsByHourTitle}
              labels={(data?.conversationsByHour ?? []).map((item) => `${item.hour}h`)}
              values={(data?.conversationsByHour ?? []).map((item) => item.count)}
              color="primary"
              emptyLabel={messages.noDataMessage}
              isLoading={isLoading}
              isRefreshing={isRefreshing}
            />
          </MDBox>
        </MDBox>
      </PageContainer>
    </PageLayout>
  );
}
